import { almostZero, Button, roundNumber } from "@xola/ui-kit";
import {
    getPartnerPackageLineItems,
    getPaymentLineItems,
    hasClaimedCancellationProtection,
    isEveryItemCanceled,
    isPartnerPackagePurchase,
    isCanceled as isPurchaseItemCanceled,
    PurchaseActionOption,
} from "common";
import { sumBy } from "lodash";
import React, { useState } from "react";
import { ExperiencePurchaseItem, Permission, Purchase, PurchaseItem } from "types";
import { isSignificantlyGreaterThan } from "../../../helpers/mathHelper";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { IsGranted } from "../../Common/IsGranted";
import { PurchaseReturnPaymentModal } from "../../PurchaseModification/Refund/PurchaseReturnPaymentModal";
import { SplitPayRefundModal } from "../../PurchaseModification/SplitPayRefundModal";
import { isOneBuyerWithSamePaymentMethod } from "@/models/Purchase";

interface ReturnExcessPaymentButtonProps {
    purchase: Purchase;
    item?: PurchaseItem;
    size?: string;
    icon?: any;
}

export const ReturnExcessPaymentButton = ({ purchase, item, size, icon }: ReturnExcessPaymentButtonProps) => {
    const invalidate = useInvalidate();
    const [currentAction, setCurrentAction] = useState<PurchaseActionOption | null>(null);

    const handleApplyAction = () => {
        invalidate("purchases", purchase.id);
        setCurrentAction(null);
    };

    const isMist = purchase.items.length > 1;
    const purchaseItem = item ?? purchase.items[0]; // Introduced this var instead of reassigning to item, since we need item-undefined logic for Mist element

    /**
     * There can be a case where purchase can be cancelled and have balance if affiliate deposit is removed
     * We should not show both collect balance and refund button, only collect balance should be displayed
     * TODO: check for other cases
     */
    const hasCollectBalance = purchase.balance > 0 && !almostZero(purchase.balance);
    const hasNegativeBalance = purchase.balance < 0 && !almostZero(purchase.balance);
    const isMistPurchaseSelected = isMist && !item;
    const showRefundExcessButton = hasNegativeBalance && (isMistPurchaseSelected || !isMist);
    const isAnyCanceled = isMist ? isEveryItemCanceled(purchase) : isPurchaseItemCanceled(purchaseItem);

    let purchaseAmount = purchase.amount;
    if (isPartnerPackagePurchase(purchase)) {
        purchaseAmount -= sumBy(getPartnerPackageLineItems(purchase), "amount");
    }

    // Cancellation Protection settled the money, so the seller has nothing left to return
    const isSettledByCancellationProtection = isMistPurchaseSelected
        ? purchase.items.some((purchaseItem) => hasClaimedCancellationProtection(purchaseItem))
        : hasClaimedCancellationProtection(purchaseItem);

    const canceledNotRefunded =
        isAnyCanceled &&
        isSignificantlyGreaterThan(purchaseAmount, purchase.balance) &&
        !hasCollectBalance &&
        !isSettledByCancellationProtection;

    const amount = purchase.balance < 0 ? purchase.balance : sumBy(getPaymentLineItems(purchase), "amount");

    return (
        <>
            {/* Show refund button if (it is canceled and has a negative balance) or just has a negative balance */}
            {showRefundExcessButton && ((hasNegativeBalance && isAnyCanceled) || !isAnyCanceled) && (
                <div className="text-right">
                    <IsGranted permissions={Permission.MODIFY_BOOKING}>
                        <Button
                            size={size}
                            color="success"
                            onClick={() => setCurrentAction(PurchaseActionOption.REFUND)}
                        >
                            <div className="flex space-x-2">
                                <span>{icon ? icon : null}</span>
                                <span>Refund Excess Payment</span>
                            </div>
                        </Button>
                    </IsGranted>
                </div>
            )}

            {canceledNotRefunded && !hasNegativeBalance && (
                <div className="text-right">
                    <IsGranted permissions={Permission.MODIFY_BOOKING}>
                        <Button
                            size={size}
                            color="success"
                            onClick={() => setCurrentAction(PurchaseActionOption.REFUND)}
                        >
                            <div className="flex space-x-2">
                                <span>{icon ? icon : null}</span>
                                <span>Return Payment</span>
                            </div>
                        </Button>
                    </IsGranted>
                </div>
            )}

            {purchaseItem && purchase.splitPayment.enabled && !isOneBuyerWithSamePaymentMethod(purchase) ? (
                <SplitPayRefundModal
                    purchase={purchase}
                    item={purchaseItem}
                    shouldReduce={false}
                    isOpen={currentAction === PurchaseActionOption.REFUND}
                    onClose={() => {
                        setCurrentAction(null);
                        invalidate("purchases", purchase.id);
                    }}
                />
            ) : (
                <PurchaseReturnPaymentModal
                    purchase={purchase}
                    item={item as ExperiencePurchaseItem}
                    refundAmount={roundNumber(purchase.currency, Math.abs(amount))}
                    shouldReduce={false}
                    isOpen={currentAction === PurchaseActionOption.REFUND}
                    onClose={() => {
                        setCurrentAction(null);
                        invalidate("purchases", purchase.id);
                    }}
                    onContinue={handleApplyAction}
                />
            )}
        </>
    );
};
